'use client';

import * as React from 'react';
import { createPortal } from 'react-dom';
import { cx } from '../utils';
import { tabbables } from '../internal/focusTrap';
import { devWarn } from '../internal/devWarn';
import { acquireScrollLock, releaseScrollLock } from '../internal/scrollLock';
import { useStableId } from '../hooks/useStableId';
import type { StyledProps, WithChildren } from '../types';
import type { UnsafeStyleProps } from '../escape-hatches';

/* Bottom-sheet escalation threshold — mirrors `@media (max-width: 480px)`
   in kit.css:2909 (Popover bottom-sheet) and `--bp-xs: 480px` in
   tokens.css. CSS media-query conditions cannot consume var(), and JS
   matchMedia strings can't either; the literal must be restated here.
   Any change to --bp-xs MUST match the kit.css media block AND this
   constant. See ADR-011 §2a. */
const BOTTOM_SHEET_MEDIA = '(max-width: 480px)';
const MOBILE_SHEET_MEDIA = '(max-width: 820px)';

/* ── Types ─────────────────────────────────────────── */

export type PopoverPlacement = 'top' | 'bottom' | 'left' | 'right';
export type PopoverAlign = 'start' | 'end' | 'center';
export type PopoverMobilePresentation = 'auto' | 'sheet';

export interface PopoverProps extends StyledProps, WithChildren, UnsafeStyleProps {
  open: boolean;
  onClose?: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
  placement?: PopoverPlacement;
  align?: PopoverAlign;
  trapFocus?: boolean;
  /** Accessible name for the popover panel. Either `ariaLabel` (a string)
   *  or `ariaLabelledBy` (an id referencing visible label text) MUST be
   *  provided so screen-reader users know what the popover contains.
   *  Audit #23 (2026-05-16): the prior API had no role/label path —
   *  consumers' popovers were unlabelled dialogs in the a11y tree.
   *  Required only when `role` is non-null. */
  ariaLabel?: string;
  /** Id of an element whose text labels the popover. Use instead of
   *  `ariaLabel` when the panel contains a visible title. */
  ariaLabelledBy?: string;
  /** ARIA id consumed by the anchor's `aria-controls` when paired with
   *  `usePopover()`. Auto-generated; override to coordinate with custom
   *  ID strategies. */
  id?: string;
  /** ARIA role for the popover panel. Defaults to `'dialog'` so most
   *  popovers get the APG dialog semantics for free. Composite consumers
   *  that render their OWN labeled dialog inside (e.g., DatePicker
   *  renders `<div role="dialog" aria-label="…">` for its calendar)
   *  should pass `panelRole={null}` to suppress the wrapper role and
   *  avoid a duplicate dialog in the a11y tree.
   *
   *  Named `panelRole` (not `role`) because the jsx-a11y/aria-role
   *  ESLint rule rejects `role={null}` statically, even though React
   *  filters undefined/null at runtime. The rename keeps the prop
   *  semantics clear while sidestepping a false-positive lint failure
   *  at every consumer call site. */
  panelRole?: 'dialog' | 'tooltip' | 'menu' | null;
  /** Escalate this popover to the mobile sheet presentation at the kit
   *  mobile breakpoint. `auto` keeps the legacy phone-only generic
   *  Popover sheet behavior; dropdown/listbox consumers should pass
   *  `sheet` so options use the same bottom-sheet affordance throughout
   *  mobile layouts without switching to Modal/dialog semantics. */
  mobilePresentation?: PopoverMobilePresentation;
}

/** Config for usePopover. `panelRole` MUST match the `panelRole` passed
 *  to the paired <Popover> — usePopover uses it to derive
 *  `aria-haspopup` on the trigger so AT users hear the right role.
 *  When `panelRole={null}` (composite consumers rendering their own
 *  dialog inside), `aria-haspopup` is omitted; the consumer's inner
 *  content should declare its own role semantics. */
export interface UsePopoverOptions {
  panelRole?: 'dialog' | 'tooltip' | 'menu' | null;
}

export interface UsePopoverReturn<TAnchor extends HTMLElement = HTMLElement> {
  anchorRef: React.RefObject<TAnchor>;
  /** Spread onto the trigger element. Includes `aria-haspopup` (derived
   *  from panelRole), `aria-expanded`, and `aria-controls` so screen-
   *  reader users get the APG-required trigger semantics. Generic over
   *  `TAnchor` so the ref's element type matches the trigger element
   *  (`<button>` defaults to HTMLButtonElement, etc.). */
  anchorProps: {
    ref: React.RefObject<TAnchor>;
    onClick: () => void;
    /* `aria-haspopup` valid values per ARIA 1.2: true | false | menu |
       listbox | tree | grid | dialog. `tooltip` is NOT a valid
       haspopup value — tooltips are inherent and don't need the
       trigger-side announcement. When panelRole='tooltip', this
       attribute is omitted from the trigger. */
    'aria-haspopup'?: 'dialog' | 'menu';
    /* Tooltips are announced via aria-describedby on the trigger (not
       aria-controls), per APG. When panelRole='tooltip', anchorProps
       emits aria-describedby={popoverId} and OMITS aria-expanded /
       aria-controls (which are dialog/menu/listbox semantics). When
       panelRole is dialog/menu/null, the dialog-style triple is
       emitted instead. */
    'aria-expanded'?: boolean;
    'aria-controls'?: string;
    'aria-describedby'?: string;
  };
  popoverProps: {
    open: boolean;
    onClose: () => void;
    anchorRef: React.RefObject<TAnchor>;
    id: string;
  };
  /** Stable id used by aria-controls / Popover#id. Same value across
   *  re-renders for a given hook instance. */
  popoverId: string;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  /** Toggle the open state. Same as the default `anchorProps.onClick`;
   *  exposed so adopters who override `anchorProps.onClick` can still
   *  reach the toggle behavior. */
  toggle: () => void;
}

/* ── Hook ──────────────────────────────────────────── */

export function usePopover<TAnchor extends HTMLElement = HTMLElement>(
  options: UsePopoverOptions = {},
): UsePopoverReturn<TAnchor> {
  const { panelRole = 'dialog' } = options;
  const [isOpen, setIsOpen] = React.useState(false);
  /* useRef<T>(null) returns RefObject<T> (non-mutable, assignable to
     JSX refs). useRef<T | null>(null) returns MutableRefObject which
     React 18 strict types reject for the `ref` JSX prop. */
  const anchorRef = React.useRef<TAnchor>(null);
  const popoverId = useStableId('jkit-popover');
  const open = React.useCallback(() => setIsOpen(true), []);
  const close = React.useCallback(() => setIsOpen(false), []);
  /* ARIA semantics by panelRole, per ARIA 1.2:
     - 'dialog' / 'menu': aria-haspopup + aria-expanded + aria-controls
       (the trigger advertises a popup that's currently open/closed)
     - 'tooltip': aria-describedby ONLY (tooltips are announced as
       descriptions, not as expandable popups; aria-haspopup="tooltip"
       isn't a valid value)
     - null: no ARIA on trigger (composite consumer owns the a11y) */
  const anchorAria: {
    'aria-haspopup'?: 'dialog' | 'menu';
    'aria-expanded'?: boolean;
    'aria-controls'?: string;
    'aria-describedby'?: string;
  } = (() => {
    if (panelRole === 'dialog' || panelRole === 'menu') {
      return {
        'aria-haspopup': panelRole,
        'aria-expanded': isOpen,
        'aria-controls': popoverId,
      };
    }
    if (panelRole === 'tooltip') {
      return { 'aria-describedby': popoverId };
    }
    return {};
  })();
  /* Toggle, not open. Bug 4: clicking the anchor a second time while
     the popover is open used to do nothing (handler was just `open`).
     The "click chip to toggle" pattern is the canonical disclosure
     semantic — Escape and outside-click are still available as
     alternate dismissals. Adopters who want open-only behavior
     (e.g. anchor activates a one-way disclosure) can still spread
     anchorProps and override onClick. */
  const toggle = React.useCallback(() => {
    if (isOpen) close();
    else open();
  }, [isOpen, open, close]);
  return {
    anchorRef,
    anchorProps: {
      ref: anchorRef,
      onClick: toggle,
      ...anchorAria,
    },
    popoverProps: { open: isOpen, onClose: close, anchorRef, id: popoverId },
    popoverId,
    isOpen,
    open,
    close,
    toggle,
  };
}

/* ── Component ─────────────────────────────────────── */

const GAP = 8;
const VIEWPORT_MARGIN = 8;

export const Popover = React.forwardRef<HTMLDivElement, PopoverProps>(function Popover(
  {
    open,
    onClose,
    anchorRef,
    placement = 'bottom',
    align = 'start',
    trapFocus = false,
    ariaLabel,
    ariaLabelledBy,
    id,
    panelRole = 'dialog',
    mobilePresentation = 'auto',
    className,
    unsafe_style,
    children,
  },
  forwardedRef,
) {
  const popRef = React.useRef<HTMLDivElement | null>(null);
  const sheetLayoutViewportHeightRef = React.useRef<number | null>(null);
  const [pos, setPos] = React.useState<{
    top: number;
    left: number;
    actual: string;
    measured: boolean;
    availableBlockSize?: number;
  }>({
    top: -9999,
    left: -9999,
    actual: placement,
    measured: false,
  });
  const [sheetViewportVars, setSheetViewportVars] = React.useState<{
    bottom: number;
    height: number;
    layoutHeight: number;
  } | null>(null);

  /* Stash onClose in a ref so effects always call the latest handler. */
  const onCloseRef = React.useRef(onClose);
  React.useEffect(() => {
    onCloseRef.current = onClose;
  });

  /* ── Position calculation ──────────────────────── */
  const updatePosition = React.useCallback(() => {
    if (!open || !anchorRef.current || !popRef.current) return;
    const anchor = anchorRef.current.getBoundingClientRect();
    /* Use offsetWidth/offsetHeight, NOT getBoundingClientRect, for the
       popover's own dimensions. The popover's entry animation (`j-pop`)
       applies `transform: scale(--scale-tapped)` at frame 0, and
       getBoundingClientRect returns the TRANSFORMED visual size.
       Measuring mid-animation undercounts the popover's layout width by
       ~5-10 px; positioning against that undercount lets the popover
       grow into the anchor when the scale animates back to 1.
       offsetWidth/offsetHeight return the LAYOUT (untransformed) size
       so the position is computed against the popover's final
       resting dimensions. Adopter feedback 2026-05-18: left-center
       popover had a 9 px overlap on first open and worse on subsequent
       opens (where wider cached content amplified the undercount).
       ResizeObserver doesn't fire for transform changes (only
       content-box changes), so it can't compensate for the animation
       on its own. */
    const pop = {
      width: popRef.current.offsetWidth,
      height: popRef.current.offsetHeight,
    };
    let top: number;
    let left: number;
    let actual = placement;
    let availableBlockSize = Math.max(0, window.innerHeight - VIEWPORT_MARGIN * 2);

    if (placement === 'bottom' || placement === 'top') {
      const availableBelow = Math.max(
        0,
        window.innerHeight - VIEWPORT_MARGIN - anchor.bottom - GAP,
      );
      const availableAbove = Math.max(0, anchor.top - VIEWPORT_MARGIN - GAP);
      /* Flip against VIEWPORT_MARGIN, not 0 / innerHeight. If neither
         side can fit the natural panel height, choose the larger side and
         expose that side's available block size as a CSS variable so
         scrollable panels (Select/Menu listboxes) cap themselves instead
         of clipping or forcing local adopter placement overrides. */
      if (placement === 'bottom') {
        top = anchor.bottom + GAP;
        availableBlockSize = availableBelow;
        if (pop.height > availableBelow && availableAbove > availableBelow) {
          top = anchor.top - Math.min(pop.height, availableAbove) - GAP;
          actual = 'top';
          availableBlockSize = availableAbove;
        }
      } else {
        top = anchor.top - pop.height - GAP;
        availableBlockSize = availableAbove;
        if (pop.height > availableAbove && availableBelow > availableAbove) {
          top = anchor.bottom + GAP;
          actual = 'bottom';
          availableBlockSize = availableBelow;
        }
      }
      if (align === 'start') left = anchor.left;
      else if (align === 'end') left = anchor.right - pop.width;
      else left = anchor.left + (anchor.width - pop.width) / 2;
    } else {
      /* Flip thresholds test against VIEWPORT_MARGIN, not 0 / innerWidth.
         If we only flipped when the popover would clip the viewport
         (i.e., < 0 or > innerWidth), the subsequent viewport-margin
         clamp at lines 239-246 still ran and would shift the popover
         IN by up to VIEWPORT_MARGIN px — eating the GAP between
         popover and anchor and sometimes collapsing it to 0. Adopter
         feedback (2026-05-18): the "Left-center" popover in the
         showcase sat directly adjacent to its button when the button
         landed within VIEWPORT_MARGIN + GAP of the viewport edge.
         Flipping at the margin (instead of at the hard clip edge)
         keeps the GAP intact in both placements. */
      if (placement === 'right') {
        left = anchor.right + GAP;
        if (left + pop.width > window.innerWidth - VIEWPORT_MARGIN) {
          left = anchor.left - pop.width - GAP;
          actual = 'left';
        }
      } else {
        left = anchor.left - pop.width - GAP;
        if (left < VIEWPORT_MARGIN) {
          left = anchor.right + GAP;
          actual = 'right';
        }
      }
      if (align === 'start') top = anchor.top;
      else if (align === 'end') top = anchor.bottom - pop.height;
      else top = anchor.top + (anchor.height - pop.height) / 2;
    }

    left = Math.max(
      VIEWPORT_MARGIN,
      Math.min(left, window.innerWidth - pop.width - VIEWPORT_MARGIN),
    );
    const effectiveBlockSize = Math.min(pop.height, availableBlockSize || pop.height);
    top = Math.max(
      VIEWPORT_MARGIN,
      Math.min(top, window.innerHeight - effectiveBlockSize - VIEWPORT_MARGIN),
    );

    setPos({ top, left, actual, measured: true, availableBlockSize });
  }, [open, placement, align, anchorRef]);

  /* ── Lifecycle: position, scroll close, resize ── */
  React.useEffect(() => {
    if (!open) return;
    /* RAF so the popover is rendered and measurable before we position. */
    const raf = requestAnimationFrame(updatePosition);
    /* Adopter feedback (2026-05-21): the previous handler closed on any
       scroll captured at window — including scrolls inside the popover
       panel itself (e.g., a long list with overflow-y: auto). Capture-
       phase goes window → descendant, so a panel-internal scroll
       short-circuited the popover on the first wheel tick before the
       inner element's own scroll handler could see it.

       Fix: filter the event target. Scrolls whose target is inside the
       popover panel are ignored; only ancestor / sibling / page scrolls
       still trigger close (the original "scroll the page out from under
       this popover" intent). The anchor is also excluded so an anchor
       that's itself scrollable (e.g., a horizontally-scrolling tag list
       trigger) doesn't self-close. The capture-phase listener is
       retained so SVG / shadow-root scrolls still bubble correctly. */
    const onScroll = (e: Event) => {
      const target = e.target;
      if (target instanceof Node) {
        if (popRef.current?.contains(target)) return;
        if (anchorRef.current?.contains(target)) return;
      }
      onCloseRef.current?.();
    };
    const onResize = () => updatePosition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);

    /* ResizeObserver re-runs the position calc whenever the popover OR
       anchor changes dimensions after the initial measure — covers:
       - web fonts swapping (DM Sans / Syne load async, text reflows
         a few px wider after initial paint)
       - dynamic content (state updates, async-loaded text/images,
         lazy-rendered children)
       - anchor resize (button label changes, parent flex re-layout)
       Without this, the popover positions ONCE based on the initial
       width and any subsequent reflow grows the popover in place,
       sliding its right edge into the anchor for left/right
       placements. Adopter feedback 2026-05-18: left-center popover
       in the showcase had 1-2 px gap (or overlap) because fonts
       loaded after the initial RAF measured.

       Feature-detect: jsdom (vitest) lacks ResizeObserver and the
       fallback path was acceptable before — the observer just
       upgrades the precision in real browsers. */
    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => updatePosition()) : null;
    if (observer) {
      if (popRef.current) observer.observe(popRef.current);
      if (anchorRef.current) observer.observe(anchorRef.current);
    }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      observer?.disconnect();
    };
  }, [open, updatePosition, anchorRef]);

  /* ── Click-outside + Escape dismiss ─────────── */
  React.useEffect(() => {
    if (!open) return;
    /* Focus return helper — mirrors Modal.tsx:160-180 with the same
       detached-node guard. Called BEFORE onClose so adopters who set
       `open=false` from their own logic can re-stash focus if they
       want to override. Modal's focus-trap effect captures
       `previouslyFocused` at mount time; Popover's anchor is the
       trigger ref, which is the equivalent semantic target (and
       stable across re-renders, unlike `document.activeElement`).
       Codex review of PR #148 (FilterButton spec) called out the
       missing focus-return as a precondition for keyboard parity
       with Modal — without it, Escape on an open Popover drops
       focus to <body> and breaks Tab order.

       Edge case: if `onClose` synchronously detaches the anchor from
       the DOM (uncommon outside vanilla-JS integrations that yank the
       trigger from its parent inside the close handler), the anchor
       receives focus here and then loses it immediately when the
       browser sees a detached focused node. The `try`/`catch` makes
       that path safe (no crash); the `document.contains(anchor)`
       guard handles React's async-unmount path but not synchronous
       removal inside the callback. */
    const returnFocus = () => {
      const anchor = anchorRef.current;
      if (anchor && typeof anchor.focus === 'function' && document.contains(anchor)) {
        try {
          anchor.focus({ preventScroll: true });
        } catch {
          /* noop — some elements refuse focus in certain states. */
        }
      }
    };
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        popRef.current &&
        !popRef.current.contains(target) &&
        anchorRef.current &&
        !anchorRef.current.contains(target)
      ) {
        returnFocus();
        onCloseRef.current?.();
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        returnFocus();
        onCloseRef.current?.();
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, anchorRef]);

  /* ── Focus trap ────────────────────────────────── */
  React.useEffect(() => {
    if (!open || !trapFocus || !popRef.current) return;
    const el = popRef.current;
    const focusable = tabbables(el);
    if (focusable.length) focusable[0]!.focus();
    const onTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const items = tabbables(el);
      if (!items.length) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    el.addEventListener('keydown', onTab);
    return () => el.removeEventListener('keydown', onTab);
  }, [open, trapFocus]);

  /* ── Scroll-lock for bottom-sheet escalation (W2) ─
     When the popover escalates to a bottom-sheet at viewport ≤480px
     (matching `@media (max-width: 480px)` in kit.css:2909), the panel
     covers the bottom 85dvh and the user's natural touch-scroll
     should NOT pass through to the background page — same contract as
     Modal. Above 480px the popover is a small anchored panel with
     plenty of background visible; scroll-lock would feel wrong (and
     close-on-scroll fires from the `scroll` listener above instead).

     Listens to the matchMedia change event so a user resizing across
     the threshold while the popover is open transitions cleanly:
     crossing INTO the sheet range acquires the lock, crossing OUT
     releases it. Without the listener, an open popover that started
     desktop and was resized to phone width would render as a sheet
     but allow background scroll-through.

     SSR-safe: matchMedia is guarded by `typeof window !== 'undefined'`
     (also covers the `if (!open || typeof document === 'undefined')`
     bailout in render — the effect only runs when `open` is true, and
     by then we know we're on the client). Reuses the shared
     `acquireScrollLock` / `releaseScrollLock` ref-counted helper so
     stacked Modal + Popover scenarios cooperate correctly. */
  React.useEffect(() => {
    if (!open) return;
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const media = mobilePresentation === 'sheet' ? MOBILE_SHEET_MEDIA : BOTTOM_SHEET_MEDIA;
    const mq = window.matchMedia(media);
    let held = false;
    const apply = (matches: boolean) => {
      if (matches && !held) {
        acquireScrollLock();
        held = true;
      } else if (!matches && held) {
        releaseScrollLock();
        held = false;
      }
    };
    apply(mq.matches);
    /* MediaQueryList event API: addEventListener('change', ...) is the
       modern path; older Safari (<14) only has the deprecated
       addListener. Feature-detect both so the popover stays correct on
       older WebKit installs that are still in the kit's support range. */
    const onChange = (e: MediaQueryListEvent) => apply(e.matches);
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange);
    } else if (
      typeof (mq as { addListener?: (cb: typeof onChange) => void }).addListener === 'function'
    ) {
      (mq as { addListener: (cb: typeof onChange) => void }).addListener(onChange);
    }
    return () => {
      if (typeof mq.removeEventListener === 'function') {
        mq.removeEventListener('change', onChange);
      } else if (
        typeof (mq as { removeListener?: (cb: typeof onChange) => void }).removeListener ===
        'function'
      ) {
        (mq as { removeListener: (cb: typeof onChange) => void }).removeListener(onChange);
      }
      if (held) {
        releaseScrollLock();
        held = false;
      }
    };
  }, [open, mobilePresentation]);

  /* ── Keyboard-aware mobile sheet viewport ─────────
     Mobile browsers can show the software keyboard by shrinking the
     visual viewport while leaving the layout viewport (and fixed
     positioning) unchanged. Sheet-mode dropdowns are fixed-positioned,
     so without a visualViewport handoff they can remain pinned behind
     the keyboard after the search row receives focus. */
  React.useEffect(() => {
    if (!open) {
      sheetLayoutViewportHeightRef.current = null;
      setSheetViewportVars(null);
      return;
    }
    if (typeof window === 'undefined') return;
    const sheetMedia = mobilePresentation === 'sheet' ? MOBILE_SHEET_MEDIA : BOTTOM_SHEET_MEDIA;
    const mq = typeof window.matchMedia === 'function' ? window.matchMedia(sheetMedia) : null;
    if (mobilePresentation !== 'sheet' && mobilePresentation !== 'auto') {
      sheetLayoutViewportHeightRef.current = null;
      setSheetViewportVars(null);
      return;
    }
    if (!mq) {
      sheetLayoutViewportHeightRef.current = null;
      setSheetViewportVars(null);
      return;
    }
    const visualViewport = window.visualViewport;
    if (!visualViewport) {
      sheetLayoutViewportHeightRef.current = null;
      setSheetViewportVars(null);
      return;
    }
    const update = () => {
      if (!mq.matches) {
        sheetLayoutViewportHeightRef.current = null;
        setSheetViewportVars(null);
        return;
      }
      const height = visualViewport.height;
      const layoutHeight = Math.max(sheetLayoutViewportHeightRef.current ?? 0, window.innerHeight);
      sheetLayoutViewportHeightRef.current = layoutHeight;
      const bottom = Math.max(0, layoutHeight - height);
      setSheetViewportVars((prev) =>
        prev &&
        prev.bottom === bottom &&
        prev.height === height &&
        prev.layoutHeight === layoutHeight
          ? prev
          : { bottom, height, layoutHeight },
      );
    };
    update();
    visualViewport?.addEventListener('resize', update);
    visualViewport?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    const onChange = () => update();
    if (mq && typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange);
    } else if (
      mq &&
      typeof (mq as { addListener?: (cb: typeof onChange) => void }).addListener === 'function'
    ) {
      (mq as { addListener: (cb: typeof onChange) => void }).addListener(onChange);
    }
    return () => {
      visualViewport?.removeEventListener('resize', update);
      visualViewport?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      if (mq && typeof mq.removeEventListener === 'function') {
        mq.removeEventListener('change', onChange);
      } else if (
        mq &&
        typeof (mq as { removeListener?: (cb: typeof onChange) => void }).removeListener ===
          'function'
      ) {
        (mq as { removeListener: (cb: typeof onChange) => void }).removeListener(onChange);
      }
    };
  }, [open, mobilePresentation]);

  /* Dev-warn if no accessible name is supplied AND panelRole is
     non-null (audit #23). When `panelRole={null}`, the consumer is
     overriding the a11y story (e.g., DatePicker provides its own
     labeled dialog inside), so this wrapper-level warn would be a
     false positive. Uses the shared devWarn helper so the
     @atelierpx/jacquard/no-bare-console lint rule isn't tripped. */
  React.useEffect(() => {
    if (open && panelRole !== null && !ariaLabel && !ariaLabelledBy) {
      devWarn(
        '[JKit.Popover] no accessible name provided. Pass `ariaLabel` (a string) or `ariaLabelledBy` (an id of a visible title) so screen-reader users know what this popover contains. Pass `panelRole={null}` if the consumer renders its own labeled dialog inside. Audit #23.',
      );
    }
  }, [open, panelRole, ariaLabel, ariaLabelledBy]);

  /* Merge the internal popRef (used for measurement) with the
     forwarded ref so adopters can attach refs for measurement /
     focus management without breaking the positioning code. */
  const setRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      popRef.current = node;
      if (typeof forwardedRef === 'function') {
        forwardedRef(node);
      } else if (forwardedRef) {
        (forwardedRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }
    },
    [forwardedRef],
  );

  if (!open || typeof document === 'undefined') return null;
  const isModalOwned = Boolean(anchorRef.current?.closest('.j-modal-sheet'));

  return createPortal(
    <div
      ref={setRef}
      id={id}
      className={cx('j-popover', className)}
      data-mobile-presentation={mobilePresentation === 'sheet' ? 'sheet' : undefined}
      data-modal-popover={isModalOwned ? 'true' : undefined}
      data-placement={pos.actual}
      role={panelRole ?? undefined}
      aria-label={panelRole !== null && !ariaLabelledBy ? ariaLabel : undefined}
      aria-labelledby={panelRole !== null ? ariaLabelledBy : undefined}
      /* aria-modal is valid ONLY on role="dialog" per ARIA 1.2. Menu/
         tooltip with trapFocus still trap focus visually but don't
         claim modal semantics. Codex audit #2 P2. */
      aria-modal={panelRole === 'dialog' && trapFocus ? 'true' : undefined}
      style={
        {
          insetBlockStart: pos.top,
          insetInlineStart: pos.left,
          ...(pos.availableBlockSize != null
            ? { '--j-popover-available-block-size': `${pos.availableBlockSize}px` }
            : null),
          ...(sheetViewportVars
            ? {
                '--j-popover-sheet-bottom': `${sheetViewportVars.bottom}px`,
                '--j-popover-visual-viewport-height': `${sheetViewportVars.height}px`,
                '--j-popover-layout-viewport-height': `${sheetViewportVars.layoutHeight}px`,
                '--j-popover-available-block-size': `calc(${sheetViewportVars.height}px - var(--sp-4xl))`,
              }
            : null),
          ...unsafe_style,
        } as React.CSSProperties
      }
      tabIndex={-1}
    >
      {children}
    </div>,
    document.body,
  );
});
